import React from 'react'
import { history } from '../store'
import { bindActionCreators } from 'redux'
import { connect } from 'react-redux'
import { push } from 'react-router-redux'
import { authLogin, authLoginGoogle, authLoginMicrosoft } from '../actions/auth'
import { GOOGLE_CLIENT_ID, MICROSOFT_CLIENT_ID } from '../config'
import rateLimiter from '../utils/rateLimiter'
import LoginLangSelector from '../components/LoginLangSelector'

// Lazily created MSAL instance (only when the Microsoft button is used).
let msalInstancePromise = null
function getMsalInstance() {
  if (!msalInstancePromise) {
    msalInstancePromise = import('@azure/msal-browser').then(async ({ PublicClientApplication }) => {
      const instance = new PublicClientApplication({
        auth: {
          clientId: MICROSOFT_CLIENT_ID,
          // "organizations" = any work/school (Entra ID) tenant, no personal accounts.
          authority: 'https://login.microsoftonline.com/organizations',
          // Redirect flow (popup breaks under Microsoft's COOP headers). Must
          // return to /login — the app root "/" is behind an auth guard that
          // redirects unauthenticated users and would wipe the response hash
          // before this view can read it. /login is public and handles it here.
          redirectUri: window.location.origin + '/login',
          navigateToLoginRequestUrl: false
        },
        cache: { cacheLocation: 'sessionStorage' }
      })
      await instance.initialize()
      // Called once (memoized): processes a pending redirect response if present.
      const redirectResult = await instance.handleRedirectPromise()
      return { instance, redirectResult }
    })
  }
  return msalInstancePromise
}

function genCaptcha() {
  const ops = ['+', '-', '×']
  const op = ops[Math.floor(Math.random() * ops.length)]
  let a, b, answer
  if (op === '+') { a = Math.ceil(Math.random() * 20); b = Math.ceil(Math.random() * 20); answer = a + b }
  else if (op === '-') { a = Math.ceil(Math.random() * 10) + 10; b = Math.ceil(Math.random() * 10); answer = a - b }
  else { a = Math.ceil(Math.random() * 9); b = Math.ceil(Math.random() * 9); answer = a * b }
  return { q: `${a} ${op} ${b} = ?`, answer }
}

const STYLES = `
.lv-wrap{display:flex;min-height:100vh;background:url(/images/login_bg.png) center/cover no-repeat;position:relative}
.lv-wrap::before{content:'';position:absolute;inset:0;pointer-events:none}
.lv-hero{flex:1;position:sticky;top:0;height:100vh;display:flex;align-items:center;justify-content:center;padding:40px;z-index:1}
.lv-hero img{max-width:480px;width:100%;opacity:.9}
.lv-panel{width:480px;min-height:100vh;background:rgba(0,18,40,.50);backdrop-filter:blur(14px);border-left:1px solid rgba(255,255,255,.12);display:flex;flex-direction:column;align-items:center;justify-content:center;padding:48px 40px;overflow-y:auto;position:relative;z-index:1}
.lv-logo{height:42px;margin-bottom:8px}
.lv-title{color:#fff;font-size:22px;font-weight:700;letter-spacing:4px;margin-bottom:24px}
.lv-subtitle{color:rgba(255,255,255,.85);font-size:13px;text-align:center;margin-bottom:24px;line-height:1.6}
.lv-form{width:100%}
.lv-field{margin-bottom:16px}
.lv-label{display:block;color:rgba(255,255,255,.7);font-size:13px;margin-bottom:6px}
.lv-input{width:100%;padding:11px 14px;border-radius:10px;border:1.5px solid rgba(255,255,255,.15);background:rgba(255,255,255,.08);color:#fff;font-size:14px;outline:none;box-sizing:border-box;transition:border-color .2s}
.lv-input::placeholder{color:rgba(255,255,255,.35)}
.lv-input:focus{border-color:rgba(255,255,255,.5)}
.lv-btn{width:100%;padding:13px;border-radius:40px;border:none;background:#fff;color:#001830;font-weight:700;font-size:15px;cursor:pointer;transition:opacity .2s;margin-top:8px}
.lv-btn:disabled{opacity:.5;cursor:not-allowed}
.lv-err{background:rgba(248,113,113,.15);border:1px solid rgba(248,113,113,.4);color:#fca5a5;padding:10px 14px;border-radius:8px;font-size:13px;margin-top:12px}
.lv-link{color:rgba(255,255,255,.55);font-size:12px;text-align:center;margin-top:14px;cursor:pointer;text-decoration:underline}
.lv-captcha-box{display:flex;align-items:center;gap:10px}
.lv-captcha-q{flex:1;padding:11px 14px;border-radius:10px;background:rgba(255,255,255,.06);border:1.5px solid rgba(255,255,255,.1);color:rgba(255,255,255,.9);font-size:15px;font-weight:600}
.lv-captcha-a{width:90px!important;flex-shrink:0}
.lv-captcha-refresh{background:none;border:none;color:rgba(255,255,255,.5);cursor:pointer;font-size:22px;padding:6px;line-height:1}
.lv-divider{display:flex;align-items:center;gap:12px;width:100%;margin:20px 0 4px;color:rgba(255,255,255,.45);font-size:12px}
.lv-divider::before,.lv-divider::after{content:'';flex:1;height:1px;background:rgba(255,255,255,.18)}
.lv-sso{position:relative;margin-top:14px;width:100%;height:40px;box-sizing:border-box;display:flex;align-items:center;justify-content:center;padding:0 44px;border-radius:40px;border:1.5px solid;font-size:14px;font-weight:500;cursor:pointer;transition:background .2s,border-color .2s}
.lv-sso svg{position:absolute;left:16px;top:50%;transform:translateY(-50%)}
.lv-sso-google{background:#fff;color:#1f1f1f;border-color:#dadce0}
.lv-sso-google:hover{background:#f7f8f8}
.lv-sso-ms{background:rgba(255,255,255,.06);color:#fff;border-color:rgba(255,255,255,.25)}
.lv-sso-ms:hover{background:rgba(255,255,255,.12);border-color:rgba(255,255,255,.45)}
.lv-sso-disabled{opacity:.5;pointer-events:none}
.lv-sso:disabled{opacity:.5;cursor:not-allowed}
.lv-gis-overlay{position:absolute;inset:0;opacity:0;overflow:hidden;border-radius:40px}
.lv-socials{margin-top:28px;display:flex;justify-content:center;align-items:center;gap:16px}
.social_logo{height:24px;width:auto;display:block}
@media(max-width:820px){.lv-hero{display:none}.lv-panel{width:100%;border-left:none;padding:32px 24px}}
`

class LoginCustomerView extends React.Component {
  constructor(props) {
    super(props)
    const cap = genCaptcha()
    this.googleBtnRef = React.createRef()
    this.state = {
      email: '', password: '',
      captchaQ: cap.q, captchaAnswer: cap.answer, captchaInput: '',
      error: '', loading: false,
      rateLimited: false, rateLimitMessage: ''
    }
  }

  componentDidMount() {
    let styleEl = document.getElementById('lv-styles')
    if (!styleEl) {
      styleEl = document.createElement('style'); styleEl.id = 'lv-styles'; document.head.appendChild(styleEl)
    }
    // Always refresh so updated rules apply even if a stale <style> tag exists.
    styleEl.textContent = STYLES
    if (rateLimiter.isRateLimited()) {
      this.setState({ rateLimited: true, rateLimitMessage: `Demasiados intentos fallidos. Espera ${rateLimiter.getFormattedTimeUntilReset()}.` })
    } else {
      this.initGoogleButton()
    }
    // Finish a Microsoft login if we've just been redirected back from Microsoft.
    this.completeMicrosoftRedirect()
  }

  initGoogleButton() {
    if (!GOOGLE_CLIENT_ID) return
    const render = () => {
      const el = this.googleBtnRef.current
      if (!el || !window.google || !window.google.accounts) return
      window.google.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: resp => this.handleGoogleCredential(resp)
      })
      // The real Google button is rendered here but sits invisible (opacity 0)
      // on top of our custom-styled button, so its click target fills it while
      // our own markup controls the appearance.
      const width = Math.min(400, el.offsetWidth || 400)
      window.google.accounts.id.renderButton(el, { theme: 'outline', size: 'large', shape: 'pill', width, text: 'signin_with' })
    }
    if (window.google && window.google.accounts) { render(); return }
    let script = document.getElementById('gsi-client')
    if (!script) {
      script = document.createElement('script')
      script.id = 'gsi-client'
      script.src = 'https://accounts.google.com/gsi/client'
      script.async = true
      script.defer = true
      document.head.appendChild(script)
    }
    script.addEventListener('load', render)
  }

  async handleGoogleCredential(response) {
    if (!response || !response.credential) return
    this.setState({ loading: true, error: '' })
    const result = await this.props.authLoginGoogle(response.credential)
    if (result && result.error) {
      this.setState({ error: result.error.message || 'Error al iniciar sesión con Google.', loading: false })
    } else {
      rateLimiter.reset()
      this.props.push('/')
    }
  }

  async handleMicrosoftLogin() {
    if (!MICROSOFT_CLIENT_ID) return
    this.setState({ loading: true, error: '' })
    try {
      const { instance } = await getMsalInstance()
      // Full-page redirect: the browser navigates away to Microsoft and comes
      // back to the app root, where completeMicrosoftRedirect() finishes login.
      await instance.loginRedirect({ scopes: ['openid', 'profile', 'email'], prompt: 'select_account' })
    } catch (err) {
      this.setState({ loading: false, error: 'No se pudo iniciar sesión con Microsoft.' })
    }
  }

  async completeMicrosoftRedirect() {
    if (!MICROSOFT_CLIENT_ID) return
    // Only load MSAL when returning from Microsoft with a response in the URL.
    const hash = window.location.hash || ''
    if (hash.indexOf('code=') === -1 && hash.indexOf('error=') === -1) return
    this.setState({ loading: true, error: '' })
    try {
      const { redirectResult } = await getMsalInstance()
      if (redirectResult && redirectResult.idToken) {
        const login = await this.props.authLoginMicrosoft(redirectResult.idToken)
        if (login && login.error) {
          this.setState({ error: login.error.message || 'Error al iniciar sesión con Microsoft.', loading: false })
        } else {
          rateLimiter.reset()
          this.props.push('/')
        }
      } else {
        this.setState({ loading: false })
      }
    } catch (err) {
      // Surface the real MSAL/server error so the cause is visible (e.g. an
      // AADSTS code or a BrowserAuthError code) instead of a generic message.
      const raw = err && (err.errorMessage || err.message || err.errorCode)
      const detail = raw ? String(raw).slice(0, 220) : 'error desconocido'
      this.setState({ loading: false, error: 'Microsoft: ' + detail })
      if (typeof console !== 'undefined' && console.error) console.error('MSAL redirect error', err)
    }
  }

  refreshCaptcha() {
    const cap = genCaptcha()
    this.setState({ captchaQ: cap.q, captchaAnswer: cap.answer, captchaInput: '' })
  }

  async handleSubmit(e) {
    e.preventDefault()
    const { email, password, captchaInput, captchaAnswer } = this.state
    if (!email || !password) { this.setState({ error: 'Email y contraseña son obligatorios.' }); return }
    if (!captchaInput) { this.setState({ error: 'Debes responder la verificación de seguridad.' }); return }
    if (parseInt(captchaInput, 10) !== captchaAnswer) {
      this.setState({ error: 'Respuesta de verificación incorrecta.' })
      this.refreshCaptcha()
      return
    }
    if (rateLimiter.isRateLimited()) { this.setState({ rateLimited: true, rateLimitMessage: `Demasiados intentos fallidos. Espera ${rateLimiter.getFormattedTimeUntilReset()}.` }); return }
    this.setState({ loading: true, error: '' })
    const result = await this.props.authLogin({ email, password }, false)
    if (result && result.error) {
      rateLimiter.recordAttempt()
      if (rateLimiter.isRateLimited()) this.setState({ rateLimited: true, rateLimitMessage: `Demasiados intentos fallidos. Espera ${rateLimiter.getFormattedTimeUntilReset()}.` })
      this.setState({ error: result.error.message || 'Error al iniciar sesión.', loading: false })
      this.refreshCaptcha()
    } else {
      rateLimiter.reset()
      this.props.push('/')
    }
  }

  render() {
    const { email, password, captchaQ, captchaInput, error, loading, rateLimited, rateLimitMessage } = this.state
    return (
      <div className="lv-wrap">
        <div className="lv-hero">
          <img src="/images/one_render.png" alt="VACWAY ONE" />
        </div>
        <div className="lv-panel">
          <LoginLangSelector />
          <img className="lv-logo" src="/images/logo.svg" alt="VACWAY ONE" />
          <div className="lv-title">WATERPROOF</div>
          {rateLimited ? (
            <div className="lv-err">⚠️ Acceso temporalmente bloqueado. {rateLimitMessage}</div>
          ) : (
            <React.Fragment>
            <form className="lv-form" onSubmit={e => this.handleSubmit(e)}>
              <div className="lv-field">
                <label className="lv-label">Email</label>
                <input className="lv-input" type="email" value={email} autoComplete="username"
                  placeholder="tu@email.com" onChange={e => this.setState({ email: e.target.value })} />
              </div>
              <div className="lv-field">
                <label className="lv-label">Contraseña</label>
                <input className="lv-input" type="password" value={password} autoComplete="current-password"
                  placeholder="••••••••" onChange={e => this.setState({ password: e.target.value })} />
              </div>
              <div className="lv-field">
                <label className="lv-label">Verificación de seguridad</label>
                <div className="lv-captcha-box">
                  <div className="lv-captcha-q">{captchaQ}</div>
                  <input className="lv-input lv-captcha-a" type="number" value={captchaInput}
                    placeholder="?" onChange={e => this.setState({ captchaInput: e.target.value })} />
                  <button type="button" className="lv-captcha-refresh" title="Nueva pregunta"
                    onClick={() => this.refreshCaptcha()}>↻</button>
                </div>
              </div>
              <div className="lv-link" onClick={() => history.push('/passwordrecoveryclient')}>¿Has olvidado tu contraseña?</div>
              {error && <div className="lv-err">{error}</div>}
              <button className="lv-btn" type="submit" disabled={loading}>
                {loading ? 'Entrando...' : 'Entrar'}
              </button>
            </form>
            {(GOOGLE_CLIENT_ID || MICROSOFT_CLIENT_ID) && <div className="lv-divider">o</div>}
            {GOOGLE_CLIENT_ID && (
              <div className={'lv-sso lv-sso-google' + (loading ? ' lv-sso-disabled' : '')}>
                <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
                  <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
                  <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
                  <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
                  <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
                </svg>
                Iniciar sesión con Google
                <div className="lv-gis-overlay" ref={this.googleBtnRef} />
              </div>
            )}
            {MICROSOFT_CLIENT_ID && (
              <button type="button" className="lv-sso lv-sso-ms" disabled={loading} onClick={() => this.handleMicrosoftLogin()}>
                <svg width="18" height="18" viewBox="0 0 21 21" aria-hidden="true">
                  <rect x="1" y="1" width="9" height="9" fill="#f25022" />
                  <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
                  <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
                  <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
                </svg>
                Iniciar sesión con Microsoft
              </button>
            )}
            </React.Fragment>
          )}
          <div className="lv-socials">
            <a href="https://vacway.com/es?utm_source=erp&utm_medium=login" target="_blank" rel="noopener noreferrer">
              <img className="social_logo" src="/images/social/www.png" alt="Web" />
            </a>
            <a href="https://es.linkedin.com/company/vacway?utm_source=erp&utm_medium=login" target="_blank" rel="noopener noreferrer">
              <img className="social_logo" src="/images/social/linkedin.png" alt="LinkedIn" />
            </a>
            <a href="https://www.instagram.com/vacway/?utm_source=erp&utm_medium=login" target="_blank" rel="noopener noreferrer">
              <img className="social_logo" src="/images/social/instagram.png" alt="Instagram" />
            </a>
            <a href="https://www.tiktok.com/@vacway?utm_source=erp&utm_medium=login" target="_blank" rel="noopener noreferrer">
              <img className="social_logo" src="/images/social/tiktok.png" alt="TikTok" />
            </a>
          </div>
        </div>
      </div>
    )
  }
}

const mapDispatchToProps = dispatch => ({
  authLogin: bindActionCreators(authLogin, dispatch),
  authLoginGoogle: bindActionCreators(authLoginGoogle, dispatch),
  authLoginMicrosoft: bindActionCreators(authLoginMicrosoft, dispatch),
  push: bindActionCreators(push, dispatch)
})

export default connect(() => ({}), mapDispatchToProps)(LoginCustomerView)
